/**
 * Fetching the pictures before anyone asks for them.
 *
 * Everything here is a bet that the family will look at what was posted
 * recently, which is nearly always true: the app opens on today's GiviSnaps
 * and the thread, and those are the same few dozen images every time. Asking
 * for them while nothing else is happening turns the first look into the
 * second look, which the service worker already answers instantly.
 *
 * Asked for as no-cors on purpose. The service worker steps aside for a cors
 * request, because the save path needs the real bytes and must not be given a
 * cached reply -- so a cors fetch here would go straight past the very cache
 * it is meant to be filling. A no-cors one is indistinguishable from what an
 * <img> asks for, which is exactly the entry wanted. What comes back is
 * opaque and useless, and is thrown away; the point was the trip, not the
 * answer.
 */
import type { MediaRef } from '../../shared/types';
import { shownMedia } from '../../shared/constants';
import { isSnapKey } from './snap';


/** Enough to cover a scroll or two, not so many that it is the whole archive. */
const MAX_WARM = 28;
/** Phones are not servers. A handful at a time leaves room for real requests. */
const AT_ONCE = 4;

let asked = new Set<string>();

/**
 * Whether it is reasonable to spend somebody's data on a guess.
 *
 * Nothing here is needed, so anything short of a good connection is a reason
 * not to: on a metered or slow one the same bytes are better spent on what is
 * actually being looked at.
 */
function worthIt(): boolean {
  const link = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;

  if (!link) return true;
  if (link.saveData) return false;
  return link.effectiveType !== 'slow-2g' && link.effectiveType !== '2g';
}

/** Runs when the browser has nothing better to do, or soon, where it cannot say. */
function whenIdle(run: () => void): void {
  const idle = (
    window as Window & { requestIdleCallback?: (cb: () => void) => number }
  ).requestIdleCallback;

  if (idle) idle(run);
  else window.setTimeout(run, 1200);
}

/**
 * Warm these urls, newest first, a few at a time.
 *
 * Safe to call again with the same list: anything already asked for this
 * session is skipped, and anything already in the cache is answered from it
 * without a request.
 */
export function warmMedia(urls: string[]): void {
  if (!('serviceWorker' in navigator) || !worthIt()) return;

  const wanted = urls.filter((url) => url && !asked.has(url)).slice(0, MAX_WARM);
  if (!wanted.length) return;
  for (const url of wanted) asked.add(url);

  whenIdle(() => {
    void (async () => {
      for (let at = 0; at < wanted.length; at += AT_ONCE) {
        await Promise.all(
          wanted.slice(at, at + AT_ONCE).map((url) =>
            fetch(url, { mode: 'no-cors', credentials: 'include' }).catch(() => {
              // A warm that fails costs nothing: the picture will be asked for
              // properly when somebody actually opens it.
              asked.delete(url);
            }),
          ),
        );
      }
    })();
  });
}

/**
 * What is worth fetching ahead, in the order it is worth having.
 *
 * Today's GiviSnaps first, because that is the screen the app opens on, and
 * the thread after it. Stills only: a clip is far too big to fetch on the
 * chance somebody plays it, and it streams rather than arriving whole, so
 * having it early buys much less than it costs. Its poster is a still and is
 * worth having, since that is what stands in for the clip until it is played.
 */
export function worthWarming(
  dailies: Array<{ back?: MediaRef | null; front?: MediaRef | null }>,
  messages: Array<{ media?: MediaRef[] }>,
): MediaRef[] {
  /*
   * Never a snap. It is for looking at once, on purpose, by someone holding it
   * open; fetching every one in the thread onto every phone at launch would be
   * the opposite. Told apart by key rather than by message kind, so it holds
   * wherever a snap's ref turns up.
   */
  const still = (media: MediaRef | null | undefined): media is MediaRef =>
    Boolean(media) &&
    (media!.kind === 'photo' || media!.kind === 'poster') &&
    !isSnapKey(media!.key);

  const out: MediaRef[] = [];

  for (const daily of dailies) {
    if (still(daily.back)) out.push(daily.back);
    if (still(daily.front)) out.push(daily.front);
  }

  // Newest first: the bottom of the thread is what opening it shows.
  for (const message of [...messages].reverse()) {
    for (const media of message.media ?? []) {
      if (still(media)) out.push(media);
    }
  }

  // The same picture can be on a daily and quoted in the thread. And it is the
  // copy the feed and the thread draw that is worth having: the small one.
  const seen = new Set<string>();
  return out.map(shownMedia).filter((media) => {
    if (seen.has(media.key)) return false;
    seen.add(media.key);
    return true;
  });
}

/** Only for tests, which would otherwise carry one run's memory into the next. */
export function forgetWarmed(): void {
  asked = new Set();
}
